/**
 * useGstRates — ViewModel for /manage/gst-rates.
 *
 * Catalog has full list + add + set-current + soft-delete (no edit:
 * rates are immutable; add a new row for a new rate).
 */

import { useCallback, useEffect, useState } from 'react'
import { useAuth } from '../context/AuthContext'
import { APIROUTES, fillPath } from '../constants/routes.constants'
import type { GstRateDto } from '../models/gst-rate.model'
import { mutateJson } from '../utils/api/mutateJson'
import { parseApiError } from '../utils/api/parseApiError'

export type { GstRateDto } from '../models/gst-rate.model'

export interface CreateGstRateInput {
  readonly cgst_bps: number
  readonly sgst_bps: number
  readonly description: string | null
  readonly set_as_current: boolean
}

export interface UseGstRatesReturn {
  readonly items: GstRateDto[]
  readonly isLoading: boolean
  readonly error: string | null
  readonly reload: () => void
  readonly add: (
    input: CreateGstRateInput
  ) => Promise<{ ok: true } | { ok: false; field: string | null; message: string }>
  readonly setCurrent: (id: string) => Promise<{ ok: true } | { ok: false; message: string }>
  readonly remove: (id: string) => Promise<{ ok: true } | { ok: false; message: string }>
}

interface ListBody {
  readonly success: true
  readonly data: { readonly items: GstRateDto[] }
}

export function useGstRates(): UseGstRatesReturn {
  const { authedFetch } = useAuth()
  const [items, setItems] = useState<GstRateDto[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const [tick, setTick] = useState<number>(0)

  useEffect(() => {
    let cancelled = false
    const run = async (): Promise<void> => {
      setIsLoading(true)
      setError(null)
      const res = await authedFetch(APIROUTES.MANAGEGSTRATES)
      if (cancelled) return
      if (!res.ok) {
        const { message } = await parseApiError(res)
        if (!cancelled) {
          setError(message)
          setIsLoading(false)
        }
        return
      }
      const body = (await res.json()) as ListBody
      if (cancelled) return
      setItems(body.data.items)
      setIsLoading(false)
    }
    void run()
    return () => { cancelled = true }
  }, [authedFetch, tick])

  const reload = useCallback((): void => setTick((t) => t + 1), [])

  const add = useCallback(
    async (input: CreateGstRateInput) => {
      const r = await mutateJson<GstRateDto>(authedFetch, APIROUTES.MANAGEGSTRATES, 'POST', input)
      if (!r.ok) return r
      reload()
      return { ok: true as const }
    },
    [authedFetch, reload]
  )

  const setCurrent = useCallback(
    async (id: string) => {
      const r = await mutateJson<GstRateDto>(
        authedFetch,
        fillPath(APIROUTES.MANAGEGSTRATESETCURRENT, { id }),
        'POST'
      )
      if (!r.ok) return { ok: false as const, message: r.message }
      reload()
      return { ok: true as const }
    },
    [authedFetch, reload]
  )

  const remove = useCallback(
    async (id: string) => {
      const r = await mutateJson<{ ok: true }>(
        authedFetch,
        fillPath(APIROUTES.MANAGEGSTRATEITEM, { id }),
        'DELETE'
      )
      if (!r.ok) return { ok: false as const, message: r.message }
      reload()
      return { ok: true as const }
    },
    [authedFetch, reload]
  )

  return { items, isLoading, error, reload, add, setCurrent, remove }
}
